<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Order</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Modify order details, items, and delivery information
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          <v-btn
                            :color="!isPending ? 'grey' : isEditMode ? 'warning' : 'primary'"
                            :outlined="!isEditMode"
                            :disabled="!isPending"
                            @click="toggleEditMode"
                            class="mr-2"
                            small
                          >
                            <v-icon left>{{ !isPending ? 'mdi-lock' : isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                            {{ !isPending ? 'Locked' : isEditMode ? 'Read Only' : 'Edit Mode' }}
                          </v-btn>
                        </span>
                      </template>
                      <span>{{ isPending ? 'Toggle edit mode' : 'Editing is only allowed for Pending orders' }}</span>
                    </v-tooltip>
                    
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Orders
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Multi-Seller Checkout Banner -->
      <v-row v-if="isMultiSellerOrder" class="mb-1">
        <v-col cols="12">
          <v-alert
            type="info"
            border="left"
            colored-border
            elevation="1"
            icon="mdi-package-variant-closed-plus"
            dense
          >
            <div class="d-flex flex-wrap align-center" style="gap: 8px">
              <span>
                This is one package from a multi-seller checkout —
                <strong>{{ form.order_group.order_number }}</strong>
                ({{ form.order_group.seller_count }} packages total).
              </span>
              <v-progress-circular v-if="isLoadingGroup" indeterminate size="16" width="2" color="primary"></v-progress-circular>
              <template v-else>
                <v-chip
                  v-for="sibling in groupSiblingOrders"
                  :key="sibling.id"
                  small
                  :outlined="sibling.id !== form.id"
                  :color="sibling.id === form.id ? 'primary' : ''"
                  @click="sibling.id !== form.id && $router.push(`/orders/${encryptIt(sibling.id)}`)"
                  :style="sibling.id !== form.id ? 'cursor:pointer' : ''"
                >
                  {{ sibling.seller ? sibling.seller.first_name : 'Seller' }} &middot; {{ sibling.order_number }}
                  <v-icon right x-small>{{ statusIcon(sibling.order_status) }}</v-icon>
                </v-chip>
              </template>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Order Information</h2>
              <v-spacer></v-spacer>
              <v-chip
                :color="!isPending ? 'grey' : isEditMode ? 'success' : 'warning'"
                :outlined="!isEditMode"
                x-small
              >
                <v-icon left x-small>{{ !isPending ? 'mdi-lock' : isEditMode ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
                {{ !isPending ? 'Locked (not Pending)' : isEditMode ? 'Edit Mode' : 'Read Only Mode' }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>
            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2" v-if="!isLoading">
          <!-- Basic Details -->
          <div class="section-block">
            <div class="section-header">
              <v-icon small color="primary" class="mr-2">mdi-receipt-text-outline</v-icon>
              <span class="section-label">Basic Details</span>
            </div>
            <v-row class="mt-2">
              <v-col sm="6">
                <v-autocomplete
                  ref="seller"
                  outlined
                  dense
                  v-model="form.seller_id"
                  :items="sellers"
                  item-text="text"
                  item-value="value"
                  label="Seller *"
                  prepend-inner-icon="mdi-account-tie"
                  :disabled="!isEditMode"
                  :readonly="!isEditMode"
                  :clearable="isEditMode"
                  :error-messages="errors.seller_id"
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  ref="patient"
                  outlined
                  dense
                  v-model="form.patient_id"
                  :items="patients"
                  item-text="text"
                  item-value="value"
                  label="Patient *"
                  prepend-inner-icon="mdi-account-heart"
                  :disabled="!isEditMode"
                  :readonly="!isEditMode"
                  :clearable="isEditMode"
                  :error-messages="errors.patient_id"
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-text-field
                  v-model="form.order_number"
                  outlined
                  dense
                  label="Order Number"
                  prepend-inner-icon="mdi-identifier"
                  :disabled="!isEditMode"
                  :readonly="!isEditMode"
                  :error-messages="errors.order_number"
                  :hint="isEditMode ? 'Leave empty to auto-generate' : ''"
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <v-textarea
                  v-model="form.address"
                  outlined
                  dense
                  label="Delivery Address"
                  prepend-inner-icon="mdi-map-marker"
                  :disabled="!isEditMode"
                  :readonly="!isEditMode"
                  :error-messages="errors.address"
                  rows="2"
                  auto-grow
                ></v-textarea>
              </v-col>
            </v-row>
          </div>

          <v-divider class="section-divider"></v-divider>

          <!-- Order Items Section -->
          <div class="section-block">
            <div class="section-header d-flex justify-space-between align-center flex-wrap">
              <div class="d-flex align-center">
                <v-icon small color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
                <span class="section-label">Order Items</span>
                <v-chip x-small outlined color="primary" class="ml-2 font-weight-medium">{{ orderItems.length }}</v-chip>
              </div>
              <v-btn
                color="primary"
                small
                text
                @click="addOrderItem"
                :disabled="!products.length || !isEditMode"
                v-if="isEditMode"
              >
                <v-icon left small>mdi-plus</v-icon>
                Add Item
              </v-btn>
            </div>

            <div v-if="orderItems.length === 0" class="empty-items">
              <v-icon large color="grey lighten-1">mdi-package-variant-closed</v-icon>
              <p class="mt-2 mb-0 text-body-2 grey--text text--darken-1">No items added yet. Click "Add Item" to get started.</p>
            </div>

            <div
              v-for="(item, index) in orderItems"
              :key="index"
              class="order-item-row"
            >
              <div class="item-index">{{ index + 1 }}</div>
              <v-row no-gutters class="flex-grow-1" align="center">
                <v-col sm="5" class="pr-sm-2">
                  <v-autocomplete
                    v-model="item.product_id"
                    :items="products"
                    item-text="name"
                    item-value="id"
                    label="Product *"
                    outlined
                    dense
                    hide-details="auto"
                    :disabled="!isEditMode"
                    :readonly="!isEditMode"
                    prepend-inner-icon="mdi-cube-outline"
                    :error-messages="getItemError(index, 'product_id')"
                    @change="onProductChange(index)"
                  >
                    <template v-slot:item="{ item: product }">
                      <div class="d-flex align-center">
                        <v-avatar size="24" class="mr-2">
                          <v-img
                            :src="product.image_path || '/images/default-product.png'"
                            :alt="product.name"
                          />
                        </v-avatar>
                        <div class="d-flex flex-column">
                          <div class="font-weight-medium">{{ product.name }}</div>
                          <div class="text-caption text-grey">
                            ₹{{ parseFloat(product.price || 0).toFixed(2) }}
                          </div>
                        </div>
                      </div>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col sm="2" class="px-sm-1">
                  <v-text-field
                    v-model.number="item.quantity"
                    label="Qty *"
                    outlined
                    dense
                    hide-details="auto"
                    type="number"
                    min="1"
                    :disabled="!isEditMode"
                    :readonly="!isEditMode"
                    :error-messages="getItemError(index, 'quantity')"
                    @input="calculateItemTotal(index)"
                  ></v-text-field>
                </v-col>
                <v-col sm="2" class="px-sm-1">
                  <v-text-field
                    v-model.number="item.product_price"
                    label="Price *"
                    outlined
                    dense
                    hide-details="auto"
                    type="number"
                    step="0.01"
                    min="0"
                    :disabled="!isEditMode"
                    :readonly="!isEditMode"
                    prepend-inner-icon="mdi-currency-inr"
                    :error-messages="getItemError(index, 'product_price')"
                    @input="calculateItemTotal(index)"
                  ></v-text-field>
                </v-col>
                <v-col sm="2" class="pl-sm-1">
                  <v-text-field
                    :value="item.total_price"
                    label="Total"
                    outlined
                    dense
                    hide-details="auto"
                    readonly
                    prefix="₹"
                    class="total-field"
                  ></v-text-field>
                </v-col>
                <v-col sm="1" class="text-right">
                  <v-btn
                    icon
                    small
                    color="error"
                    @click="removeOrderItem(index)"
                    v-if="isEditMode"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </div>

          <v-divider class="section-divider"></v-divider>

          <!-- Order Summary -->
          <div class="section-block">
            <div class="section-header">
              <v-icon small color="primary" class="mr-2">mdi-calculator-variant-outline</v-icon>
              <span class="section-label">Order Summary</span>
            </div>
            <v-row class="mt-2">
              <v-col cols="12" md="7">
                <v-row>
                  <v-col sm="6">
                    <v-text-field
                      v-model="form.tax_percentage"
                      outlined
                      dense
                      label="Tax Percentage (%)"
                      prepend-inner-icon="mdi-percent"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.tax_percentage"
                      type="number"
                      min="0"
                      max="100"
                      @input="calculateAmounts"
                    ></v-text-field>
                  </v-col>
                  <v-col sm="6">
                    <v-text-field
                      v-model="form.delivery_charge"
                      outlined
                      dense
                      label="Delivery Charge"
                      prepend-inner-icon="mdi-truck-delivery"
                      :disabled="!isEditMode"
                      :readonly="!isEditMode"
                      :error-messages="errors.delivery_charge"
                      type="number"
                      step="0.01"
                      min="0"
                      @input="calculateAmounts"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="5">
                <div class="summary-panel">
                  <div class="summary-row">
                    <span>Subtotal</span>
                    <span>₹{{ calculatedSubtotal }}</span>
                  </div>
                  <div class="summary-row">
                    <span>Tax ({{ form.tax_percentage || 0 }}%)</span>
                    <span>₹{{ calculatedTaxAmount }}</span>
                  </div>
                  <div class="summary-row">
                    <span>Delivery Charge</span>
                    <span>₹{{ parseFloat(form.delivery_charge || 0).toFixed(2) }}</span>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="summary-row summary-total">
                    <span>Final Amount</span>
                    <span>₹{{ form.final_amount }}</span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <v-divider class="section-divider"></v-divider>

          <!-- Payment & Status Information -->
          <div class="section-block">
            <div class="section-header">
              <v-icon small color="primary" class="mr-2">mdi-credit-card-outline</v-icon>
              <span class="section-label">Payment &amp; Status</span>
            </div>
            <v-row class="mt-2">
              <v-col sm="4">
                <v-autocomplete
                  ref="payment_mode"
                  outlined
                  dense
                  v-model="form.payment_mode"
                  :items="paymentModes"
                  label="Payment Mode"
                  :disabled="!isEditMode"
                  :readonly="!isEditMode"
                  prepend-inner-icon="mdi-credit-card-outline"
                  :error-messages="errors.payment_mode"
                ></v-autocomplete>
              </v-col>
              <v-col sm="4">
                <v-autocomplete
                  ref="payment_status"
                  outlined
                  dense
                  v-model="form.payment_status"
                  :items="paymentStatuses"
                  label="Payment Status"
                  :disabled="!isEditMode"
                  :readonly="!isEditMode"
                  prepend-inner-icon="mdi-cash-check"
                  :error-messages="errors.payment_status"
                ></v-autocomplete>
              </v-col>
              <v-col sm="4">
                <v-autocomplete
                  ref="order_status"
                  outlined
                  dense
                  v-model="form.order_status"
                  :items="orderStatuses"
                  label="Order Status"
                  :disabled="!isEditMode"
                  :readonly="!isEditMode"
                  prepend-inner-icon="mdi-package-variant"
                  :error-messages="errors.order_status"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </div>

          <!-- Timestamps (if editing existing order) -->
          <template v-if="form.delivered_at || form.cancelled_at">
            <v-divider class="section-divider"></v-divider>
            <div class="section-block">
              <div class="section-header">
                <v-icon small color="primary" class="mr-2">mdi-clock-outline</v-icon>
                <span class="section-label">Order Timeline</span>
              </div>
              <v-row class="mt-2">
                <v-col sm="6" v-if="form.delivered_at">
                  <v-text-field
                    v-model="form.delivered_at"
                    outlined
                    dense
                    label="Delivered At"
                    prepend-inner-icon="mdi-truck-check"
                    readonly
                  ></v-text-field>
                </v-col>
                <v-col sm="6" v-if="form.cancelled_at">
                  <v-text-field
                    v-model="form.cancelled_at"
                    outlined
                    dense
                    label="Cancelled At"
                    prepend-inner-icon="mdi-close-circle"
                    readonly
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </template>


              <!-- Form Actions Footer -->
              <v-divider></v-divider>
              <v-card-actions class="form-actions pa-2" v-if="isEditMode">
                <v-row class="w-100 ma-0" align="center">
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex align-center">
                      <v-chip 
                        v-if="orderItems.length === 0"
                        small 
                        color="error" 
                        text-color="white"
                        class="mr-3"
                      >
                        <v-icon left x-small>mdi-alert-circle</v-icon>
                        Please add at least one order item
                      </v-chip>
                      <span class="text-caption grey--text" v-else>
                        {{ orderItems.length }} item(s) in order
                      </span>
                    </div>
                  </v-col>
                  
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex justify-end">
                      <v-btn
                        color="grey"
                        outlined
                        class="mr-3 action-button"
                        @click="$router.back()"
                        :disabled="isLoading"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        color="primary"
                        class="action-button"
                        @click="save"
                        :loading="isLoading"
                        :disabled="isLoading || orderItems.length === 0"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Update Order
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

        <!-- Loading State -->
        <v-row v-if="isLoading">
          <v-col cols="12">
            <v-card elevation="3" class="form-card">
              <v-card-text class="py-12 text-center">
                <health-loader message="Loading order details..." />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
export default {
  name: "OrderEditPage",
  data() {
    return {
      form: {
        seller_id: "",
        patient_id: "",
        order_number: "",
        address: "",
        tax_percentage: 0,
        delivery_charge: 0,
        final_amount: 0,
        payment_mode: false,
        payment_status: false,
        order_status: false,
        is_active: true,
        delivered_at: null,
        cancelled_at: null,
      },
      orderItems: [],
      deletedOrderItems: [],
      sellers: [],
      patients: [],
      products: [],
      paymentModes: [
        { id: "CASH", text: "Cash", value: false },
        { id: "ONLINE", text: "Online", value: true },
      ],
      paymentStatuses: [
        { id: "UNPAID", text: "Unpaid", value: false },
        { id: "PAID", text: "Paid", value: true },
      ],
      orderStatuses: [
        { id: "PENDING", text: "Pending", value: false },
        { id: "PROCESSED", text: "Processed", value: true },
      ],
      isLoading: false,
      isEditMode: false,
      groupSiblingOrders: [],
      isLoadingGroup: false,
    };
  },
  computed: {
    calculatedSubtotal() {
      return this.orderItems.reduce((total, item) => {
        return total + (parseFloat(item.total_price) || 0);
      }, 0).toFixed(2);
    },
    calculatedTaxAmount() {
      const subtotal = parseFloat(this.calculatedSubtotal);
      const taxPercentage = parseFloat(this.form.tax_percentage) || 0;
      return (subtotal * taxPercentage / 100).toFixed(2);
    },
    isPending() {
      return parseInt(this.form.order_status) === 0;
    },
    isMultiSellerOrder() {
      return !!(this.form.order_group && this.form.order_group.seller_count > 1);
    },
  },
  mounted() {
    this.getUsers();
    this.getProducts();
    this.getData();
  },
  methods: {
    toggleEditMode() {
      if (!this.isPending) {
        this.$toast.warning("Only Pending orders can be edited.");
        return;
      }
      this.isEditMode = !this.isEditMode;
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info("Read-only mode enabled. Form is now protected from changes.");
      }
    },

    async getUsers() {
      try {
        const response = await axios.get("users", {
          params: { rowsPerPage: -1, is_active: 1, show_deleted: 0 }
        });
        const users = response.data.data;
        
        // Filter users by position - sellers and patients
        const sellerUsers = users.filter(user => 
          user.position && user.position.name && 
          (user.position.name.toLowerCase().includes('seller') || 
           user.position.name.toLowerCase().includes('sales') ||
           user.position.name.toLowerCase().includes('agent'))
        );
        
        const patientUsers = users.filter(user => 
          user.position && user.position.name && 
          user.position.name.toLowerCase().includes('patient')
        );
        
        const displayName = (user) =>
          [user.first_name, user.last_name].filter(Boolean).join(' ') ||
          user.user_name || user.email || `User #${user.id}`;

        // Populate sellers dropdown with users having seller positions
        this.sellers = sellerUsers.map(user => ({
          text: displayName(user),
          value: user.id,
        }));

        // Populate patients dropdown with users having patient positions
        this.patients = patientUsers.map(user => ({
          text: displayName(user),
          value: user.id,
        }));

        // If no users found with specific positions, show all users in both dropdowns as fallback
        if (this.sellers.length === 0 && this.patients.length === 0) {
          this.sellers = users.map(user => ({
            text: displayName(user),
            value: user.id,
          }));

          this.patients = users.map(user => ({
            text: displayName(user),
            value: user.id,
          }));
        }
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    async getProducts() {
      try {
        const response = await axios.get("/products", {
          params: { rowsPerPage: -1, is_active: 1, show_deleted: 0 }
        });
        this.products = response.data.data.map(product => ({
          id: product.id,
          name: product.name,
          price: product.price || 0,
          text: `${product.name} - ₹${product.price || 0}`,
          value: product.id
        }));
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.orderId;
      try {
        const response = await axios.get(`/orders/${encryptedId}`, {
          params: { context: 'panel' }
        });
        this.form = response.data.data;

        // Map API field names to form field names
        this.form.patient_id = this.form.customer_id;

        // Order items are already included in the panel response via the relationship
        const rawItems = this.form.order_items || [];
        this.orderItems = rawItems.map(item => ({
          id: item.id,
          product_id: item.product_id,
          quantity: parseInt(item.quantity) || 1,
          product_price: parseFloat(item.unit_price) || 0,
          total_price: parseFloat(item.total_price) || 0,
          is_active: item.is_active !== false,
        }));

        // Format dates for display
        if (this.form.delivered_at) {
          this.form.delivered_at = new Date(this.form.delivered_at).toLocaleString();
        }
        if (this.form.cancelled_at) {
          this.form.cancelled_at = new Date(this.form.cancelled_at).toLocaleString();
        }

        // Calculate amounts based on existing items
        this.calculateAmounts();

        if (this.isMultiSellerOrder) {
          this.fetchGroupSiblings();
        }

      } catch (error) {
        console.error("Error fetching order data:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
      this.isLoading = false;
    },
    async fetchGroupSiblings() {
      if (!this.form.order_group) return;
      this.isLoadingGroup = true;
      try {
        const response = await axios.get(`/order-groups/${this.encryptIt(this.form.order_group.id)}`);
        this.groupSiblingOrders = response.data?.data?.orders || [];
      } catch (error) {
        console.error("Error fetching sibling packages:", error);
      }
      this.isLoadingGroup = false;
    },
    statusIcon(status) {
      const icons = {
        0: "mdi-clock-outline",
        1: "mdi-cog-outline",
        2: "mdi-package-variant-closed",
        3: "mdi-truck-outline",
        4: "mdi-truck-fast-outline",
        5: "mdi-truck-fast",
        6: "mdi-check-circle",
        7: "mdi-alert-circle-outline",
        8: "mdi-truck-remove-outline",
        9: "mdi-truck-remove",
        10: "mdi-truck-check-outline",
        11: "mdi-cancel",
        12: "mdi-credit-card-off-outline",
      };
      return icons[parseInt(status)] || "mdi-help-circle-outline";
    },
    addOrderItem() {
      this.orderItems.push({
        product_id: null,
        quantity: 1,
        product_price: 0,
        total_price: 0,
        is_active: true,
      });
    },
    removeOrderItem(index) {
      const item = this.orderItems[index];
      // If it's an existing item (has ID), mark it for deletion
      if (item.id) {
        this.deletedOrderItems.push(item.id);
      }
      this.orderItems.splice(index, 1);
      this.calculateAmounts();
    },
    onProductChange(index) {
      const item = this.orderItems[index];
      const product = this.products.find(p => p.id === item.product_id);
      if (product) {
        item.product_price = parseFloat(product.price || 0);
        this.calculateItemTotal(index);
      }
    },
    calculateItemTotal(index) {
      const item = this.orderItems[index];
      const quantity = parseInt(item.quantity) || 0;
      const price = parseFloat(item.product_price) || 0;
      item.total_price = (quantity * price).toFixed(2);
      this.calculateAmounts();
    },
    calculateAmounts() {
      // Calculate subtotal from order items
      const subtotal = parseFloat(this.calculatedSubtotal);
      const taxPercentage = parseFloat(this.form.tax_percentage) || 0;
      const deliveryCharge = parseFloat(this.form.delivery_charge) || 0;
      
      const taxAmount = (subtotal * taxPercentage) / 100;
      this.form.final_amount = (subtotal + taxAmount + deliveryCharge).toFixed(2);
    },
    getItemError(index, field) {
      return this.errors[`order_items.${index}.${field}`] || [];
    },
    async save() {
      try {
        this.isLoading = true;
        this.errors = {};
        
        // Ensure numeric values are properly formatted
        const formData = {
          ...this.form,
          tax_percentage: parseInt(this.form.tax_percentage) || 0,
          delivery_charge: parseFloat(this.form.delivery_charge) || 0,
          final_amount: parseFloat(this.form.final_amount) || 0,
          total_amount: parseFloat(this.calculatedSubtotal) || 0,
          tax_amount: parseFloat(this.calculatedTaxAmount) || 0,
          order_items: this.orderItems.map(item => ({
            id: item.id || null,
            product_id: item.product_id,
            quantity: parseInt(item.quantity) || 1,
            product_price: parseFloat(item.product_price) || 0,
            is_active: item.is_active,
          })),
          deleted_order_items: this.deletedOrderItems,
        };
        
        // Remove date fields as they should not be updated manually
        delete formData.delivered_at;
        delete formData.cancelled_at;
        
        await axios.patch(`/orders/${this.$route.params.orderId}`, formData);
        this.isLoading = false;
        this.$router.push("/orders").catch(() => {});
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        } else {
          console.error("Error updating order:", error);
        }
      }
    },
  },
};
</script>

<style scoped>
/* Professional Page Header */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

/* Professional Form Card */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin: 0;
}

.form-content {
  background: #ffffff;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Section blocks — flat, divided sections instead of nested cards */
.section-block {
  padding: 20px 4px 28px;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.section-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
}

.section-divider {
  margin: 0 4px;
}

/* Order Item Rows */
.empty-items {
  text-align: center;
  padding: 32px 0;
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 8px;
}

.order-item-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 12px 4px;
  margin-bottom: 8px;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}

.order-item-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.item-index {
  flex: none;
  width: 24px;
  height: 24px;
  margin-top: 6px;
  border-radius: 50%;
  background: var(--v-primary-base);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.total-field .v-input__control {
  background-color: rgba(0, 0, 0, 0.03);
}

/* Order Summary receipt panel */
.summary-panel {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 16px 18px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.7);
  padding: 4px 0;
}

.summary-total {
  font-size: 1rem;
  font-weight: 700;
  color: var(--v-primary-base);
  padding-top: 2px;
}
</style>